import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { IconButton, Dialog, DialogContent, Chip } from "@mui/material";
import { Close } from "@mui/icons-material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import Axios from "../axios";

const formatAddress = (address = {}) =>
  [address.line1, address.city, address.state, address.postal_code, address.country]
    .filter(Boolean)
    .join(", ") || "-";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const STATUS_COLOR = { paid: "success", pending: "warning", failed: "error" };

const PurchaseOrders = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  const fetchOrders = useCallback(
    (pageIndex, searchText) => {
      setLoading(true);
      Axios.get(`${BACKEND_URL}purchase-orders`, {
        params: { page: pageIndex + 1, limit: 10, search: searchText },
      })
        .then((res) => {
          setRows(res?.data?.data?.docs || []);
          setCount(res?.data?.data?.totalDocs || 0);
        })
        .catch(showError)
        .finally(() => setLoading(false));
    },
    [BACKEND_URL]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrders(0, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders]);

  const handleOpen = (row) => {
    setViewData(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setViewData(null);
  };

  const columns = [
    {
      name: "createdAt",
      label: "Order Date",
      options: { customBodyRenderLite: (i) => formatDateTime(rows[i]?.createdAt) },
    },
    {
      name: "customerName",
      label: "Customer Name",
      options: { sort: false, customBodyRenderLite: (i) => rows[i]?.customer?.fullName || "-" },
    },
    {
      name: "customerEmail",
      label: "Customer Email",
      options: { sort: false, customBodyRenderLite: (i) => rows[i]?.customer?.email || "-" },
    },
    {
      name: "raceNames",
      label: "Race-cation",
      options: {
        sort: false,
        customBodyRenderLite: (i) => {
          const names = (rows[i]?.raceNames || []).filter(Boolean);
          return names.length ? names.join(", ") : "-";
        },
      },
    },
    {
      name: "items",
      label: "Items",
      options: {
        sort: false,
        customBodyRenderLite: (i) => {
          const items = rows[i]?.items || [];
          if (!items.length) return "-";
          const first = `${items[0].name} x${items[0].qty}`;
          return items.length > 1 ? `${first} +${items.length - 1} more` : first;
        },
      },
    },
    {
      name: "total",
      label: "Total",
      options: { customBodyRenderLite: (i) => money(rows[i]?.total) },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => (
          <Chip
            size="small"
            label={rows[i]?.status || "-"}
            color={STATUS_COLOR[rows[i]?.status] || "default"}
          />
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        customBodyRenderLite: (i) => (
          <IconButton onClick={() => handleOpen(rows[i])} aria-label="View order details">
            <RemoveRedEyeOutlinedIcon className="icon-size" />
          </IconButton>
        ),
      },
    },
  ];

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Purchase Orders</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Purchase Orders</h2>
      </div>

      <MUIDataTable
        title="All purchases across every race-cation"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          search: true,
          viewColumns: false,
          sort: false,
          serverSide: true,
          count,
          page,
          rowsPerPageOptions: [],
          download: false,
          textLabels: {
            body: {
              noMatch: loading ? "Loading..." : "No purchases yet.",
            },
          },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
              fetchOrders(tableState.page, search);
            }
          },
          onSearchChange: (searchText) => {
            const searchValue = searchText || "";
            setSearch(searchValue);
            setPage(0);
            fetchOrders(0, searchValue);
          },
        }}
      />

      <Dialog onClose={handleClose} aria-labelledby="purchase-order-details" fullWidth maxWidth="md" open={open}>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Order Details</h3>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          {viewData ? (
            <div className="viewDetials">
              <div className="row">
                <div className="col-md-12">
                  <div className="d-flex justify-content-between mb-3">
                    <p className="fs-16 fw-600 mb-3">
                      Order ID :
                      <span className="fw-500 fs-18 ms-1">{viewData.orderId}</span>
                    </p>
                    <p className="fs-16 fw-600 mb-3">
                      Order Date :
                      <span className="fw-500 fs-18 ms-1">{formatDateTime(viewData.createdAt)}</span>
                    </p>
                  </div>

                  <p className="fs-16 fw-600 mb-3">
                    Status :
                    <Chip
                      size="small"
                      className="ms-2"
                      label={viewData.status}
                      color={STATUS_COLOR[viewData.status] || "default"}
                    />
                  </p>

                  <p className="fs-16 fw-600 mb-3">
                    Race-cation :
                    <span className="fw-500 fs-18 ms-1">
                      {(viewData.raceNames || []).filter(Boolean).join(", ") || "-"}
                    </span>
                  </p>

                  <div className="mt-4">
                    <p className="fs-16 fw-600 mb-2">Customer</p>
                    <p className="fs-14 mb-1">Name: <span className="fw-500">{viewData.customer?.fullName || "-"}</span></p>
                    <p className="fs-14 mb-1">Email: <span className="fw-500">{viewData.customer?.email || "-"}</span></p>
                    <p className="fs-14 mb-1">Phone: <span className="fw-500">{viewData.customer?.phone || "-"}</span></p>
                    <p className="fs-14 mb-1">
                      Shipping / Billing Address: <span className="fw-500">{formatAddress(viewData.customer?.address)}</span>
                    </p>
                  </div>

                  <div className="mt-4">
                    <p className="fs-16 fw-600 mb-2">Items</p>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Race-cation</th>
                          <th>Qty</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewData.items || []).map((item, index) => {
                          const race = (viewData.races || []).find(
                            (r) => String(r._id) === String(item.raceId)
                          );
                          return (
                            <tr key={index}>
                              <td>{item.name}</td>
                              <td>{race?.raceName || "-"}</td>
                              <td>{item.qty}</td>
                              <td>{money(item.price)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4">
                    <p className="fs-14 mb-1 d-flex justify-content-between">
                      <span>Subtotal</span> <span>{money(viewData.subtotal)}</span>
                    </p>
                    <p className="fs-14 mb-1 d-flex justify-content-between">
                      <span>Tax</span> <span>{money(viewData.tax)}</span>
                    </p>
                    <p className="fs-14 mb-1 d-flex justify-content-between">
                      <span>Shipping</span> <span>{money(viewData.shipping)}</span>
                    </p>
                    <p className="fs-16 fw-600 d-flex justify-content-between">
                      <span>Total</span> <span>{money(viewData.total)}</span>
                    </p>
                  </div>

                  <p className="fs-14 mb-1 mt-4">
                    Payment Intent ID: <span className="fw-500">{viewData.paymentIntentId || "-"}</span>
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
